import React from 'react';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { Trash2, Copy, Download, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { InvoiceService } from '../services/invoiceService';
import { useProject } from '@/components/ProjectSelector';
import { Invoice } from '../types';
import { trackInvoiceCreated, trackInvoiceUpdated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { InvoiceForm } from './InvoiceForm';
import { uploadInvoiceAttachments, deleteInvoiceAttachment } from '../services/invoiceAttachments';



export function InvoicesTable() {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every invoice the user may see; the counters and each invoice's amount paid come with the row.
  const list = useEntityList<any, Invoice>({
    entityType: 'invoice',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    includeRollups: true,
    defaultSort: { columnKey: 'invoice_id', direction: 'asc', sortKey: 'invoice_id' },
    mapRow: (row) => row,
  });
  const invoices = list.items;
  const { projects } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  const [showForm, setShowForm] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [currentInvoice, setCurrentInvoice] = React.useState<Invoice | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);

  const handleNewInvoice = async () => {
    if (!companyId || !user) return;
    
    try {
      // Don't auto-generate number - it's user input
      setCurrentInvoice({
        id: '',
        invoice_id: '', // backend-generated on create, unknown until then
        company_id: companyId,
        project_id: '',
        number: '', // User input - leave empty
        status: 'open',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        total_amount: 0,
        notes: '',
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        name: '',
        type: 'Invoice',
        description: '',
        tax_pct: 0,
        markup_pct: 0,
        other_amount: 0
      });
      setShowForm(true);
    } catch (error) {
      console.error('Error creating new invoice:', error);
    }
  };

  const handleEditInvoice = async (invoice: Invoice) => {
    // Open form immediately with basic invoice data
    setCurrentInvoice(invoice);
    setShowForm(true);
    
    // Load full invoice data asynchronously (items, attachments will be loaded by InvoiceForm)
    if (!companyId) return;
    try {
      const invoiceData = await InvoiceService.getInvoice(invoice.id);
      if (invoiceData) {
        setCurrentInvoice({ 
          ...invoiceData.invoice, 
          items: invoiceData.items,
        } as any);
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await InvoiceService.deleteInvoice(invoiceId);
      list.reload();
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };


  const handleSaveInvoice = async ({ invoiceData, newAttachments, removedAttachmentIds }: { invoiceData: any; newAttachments?: Array<{ file: File; name: string; size: number; type: string }>; removedAttachmentIds?: string[] }) => {
    if (!companyId || !user) return;
    
    try {
      let savedInvoice;
      if (currentInvoice?.id) {
        // Update existing invoice
        savedInvoice = await InvoiceService.updateInvoice({
          id: currentInvoice.id,
          ...invoiceData
        });
      } else {
        // Create new invoice
        savedInvoice = await InvoiceService.createInvoice({ ...invoiceData, company_id: companyId });
      }

      // Handle new attachments
      if (newAttachments?.length) {
        await uploadInvoiceAttachments(
          savedInvoice.id,
          newAttachments.map((a: any) => a.file),
          companyId,
          user.id
        );
      }

      // Handle removed attachments
      if (removedAttachmentIds?.length) {
        for (const attachmentId of removedAttachmentIds) {
          try {
            await deleteInvoiceAttachment(attachmentId, companyId, user.id, undefined);
          } catch (attachmentDeleteError) {
            console.error('Error deleting attachment:', attachmentDeleteError);
          }
        }
      }
      
      list.reload();
      
      // Track successful invoice creation/update with PostHog
      if (currentInvoice?.id) {
        trackInvoiceUpdated({
          invoice_id: savedInvoice.id,
          invoice_number: invoiceData.number,
          invoice_type: invoiceData.type,
          project_id: invoiceData.project_id,
          status: invoiceData.status,
          total_amount: invoiceData.total_amount,
          attachment_count: newAttachments?.length || 0,
          company_id: companyId,
          updated_by: user.id
        });
      } else {
        trackInvoiceCreated({
          invoice_id: savedInvoice.id,
          invoice_number: invoiceData.number,
          invoice_type: invoiceData.type,
          project_id: invoiceData.project_id,
          status: invoiceData.status,
          total_amount: invoiceData.total_amount,
          attachment_count: newAttachments?.length || 0,
          company_id: companyId,
          created_by: user.id
        });
      }

      // Track successful form submission
      trackFormSubmission({
        form_type: currentInvoice?.id ? 'invoice_update' : 'invoice_creation',
        success: true
      });
      
      return savedInvoice;
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      
      // Track failed invoice save with PostHog
      trackFormSubmission({
        form_type: currentInvoice?.id ? 'invoice_update' : 'invoice_creation',
        success: false,
        error_message: error.message || 'Failed to save invoice'
      });
      
      throw error;
    }
  };


  // Handle copy selected invoices
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one invoice to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy invoices');
      return;
    }

    try {
      const selectedInvs = invoices.filter(inv => selectedRows.includes(inv.id));
      let successCount = 0;
      let errorCount = 0;

      // Copy each selected invoice - let API generate invoice_id automatically
      for (const inv of selectedInvs) {
        try {
          const response = await fetch(getApiUrl('api/entities/invoice'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              // Don't include invoice_id - let API generate it
              project_id: inv.project_id || null,
              number: inv.number, // Keep user's invoice number
              name: inv.name || null,
              type: inv.type || null,
              issue_date: inv.issue_date,
              due_date: inv.due_date || null,
              status: 'draft', // Always set to draft for copies
              total_amount: inv.total_amount || 0,
              notes: inv.notes || null,
              description: inv.description || null,
              tax_pct: inv.tax_pct || null,
              markup_pct: inv.markup_pct || null,
              other_amount: inv.other_amount || null,
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error copying invoice:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error copying invoice:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Copied ${successCount} invoice(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} invoice(s).`);
      }
    } catch (error) {
      console.error('Error copying invoices:', error);
      alert('Error copying invoices. Please try again.');
    }
  };

  // Handle delete selected invoices
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one invoice to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} invoice(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete invoices');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const invoiceId of selectedRows) {
        try {
          const response = await fetch(getApiUrl(`api/entities/invoice/${invoiceId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              deleted_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error deleting invoice:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error deleting invoice:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Deleted ${successCount} invoice(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully deleted ${successCount} invoice(s).`);
      }
    } catch (error) {
      console.error('Error deleting invoices:', error);
      alert('Error deleting invoices. Please try again.');
    }
  };

  // Export to CSV: the invoices selected in the table, or -- with nothing selected -- EVERY invoice of the current
  // project + search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'invoices', 'invoices');

  const formatInvoiceId = (invoice: Invoice): string => invoice.invoice_id;

  // Map invoice statuses to GenericTag status types
  const mapInvoiceStatusToTag = (status: string): 'draft' | 'open' | 'sent' | 'paid' | 'partially_paid' | 'void' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'open':
        return 'open';
      case 'sent':
        return 'sent';
      case 'paid':
        return 'paid';
      case 'partially_paid':
        return 'partially_paid';
      case 'void':
        return 'void';
      default:
        return 'draft';
    }
  };

  // Format status text with proper capitalization
  const formatInvoiceStatusText = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<Invoice>[] = [
    {
      key: 'invoice_id',
      header: 'Invoice ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'invoice_id',
      render: (row) => <span className="font-medium">{formatInvoiceId(row)}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      width: '12rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => row.name || 'Untitled Invoice',
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '10rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => projects.find((p) => p.id === row.project_id)?.name || 'Unknown Project',
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapInvoiceStatusToTag(row.status)}
        >
          {formatInvoiceStatusText(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: '7rem',
      sortable: true,
      sortKey: 'due_date',
      render: (row) => row.due_date ? new Date(row.due_date).toLocaleDateString() : '-',
    },
    {
      key: 'amount_paid',
      header: 'Amount Paid',
      width: '7rem',
      sortable: true,
      sortKey: 'amount_paid',
      align: 'right' as const,
      render: (row) => `$${Number((row as any).amount_paid || 0).toFixed(2)}`,
    },
    {
      key: 'balance_due',
      header: 'Balance Due',
      width: '7rem',
      sortable: true,
      sortKey: 'balance_due',
      align: 'right' as const,
      render: (row) => `$${Math.max(0, Number(row.total_amount || 0) - Number((row as any).amount_paid || 0)).toFixed(2)}`,
    },
    {
      key: 'total_amount',
      header: 'Amount',
      width: '7rem',
      align: 'right' as const,
      sortable: true,
      sortKey: 'total_amount',
      render: (row) => `$${Number(row.total_amount || 0).toFixed(2)}`,
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => <span className="text-center block">{(row as any).attachment_count || 0}</span>,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'comment_count',
      render: (row) => <span className="text-center block">{(row as any).comment_count || 0}</span>,
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton
            label="New"
            onClick={() => {
              trackButtonClick('new_invoice_button', { context: 'cost_management' });
              handleNewInvoice();
            }}
          />
          <SearchButton
            onSearch={list.setSearchInput}
            placeholder="Search"
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      {/* Generic Table */}
      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1200}
        onRowClick={handleEditInvoice}
        selectable={true}
        selectedKeys={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      {showForm && (
        <InvoiceForm
          invoice={currentInvoice}
          projects={projects}
          onClose={() => {
            setShowForm(false);
            setCurrentInvoice(null);
            setHasUnsavedChanges(false);
          }}
          onSave={handleSaveInvoice}
          onChanged={() => list.reload()}
          isOpen={showForm}
        />
      )}
    </div>
  );
}
